import { CreateTick, PartnerDetailEdit as PartnerDetailEditIcon } from 'common/components/svgs';
import { useQuery, useMutation } from '@apollo/client';
import {
  Breadcrumb,
  message,
  Button,
  Spin,
  Table,
  Tabs,
  Tag,
  Icon,
  Divider,
  Empty,
  Tooltip,
  Card,
  Descriptions,
  Popover,
} from 'antd';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { DeleteOutlined, FormOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCurrentRoles } from '~components/switch-role/useSwitchRole';
import { dayFormatType, formatDay } from '~helpers/day';
import { DELETE_PARTNER_PROMOTION } from '~pages/dashboard/referral/partner-account/queries';
import { QUERY_LIST_PARTNERS } from '~pages/dashboard/referral/partner-account/queries';
import generatePath from '~settings/routing';
import PartnerCommissionPlanEditor from './partner-commission-plan-editor';
import PartnerAccountOwnerEditor from '~pages/dashboard/referral/partner-account/detail/account-owner-editor';
import PartnerAccountStatus from '~pages/dashboard/referral/partner-account/detail/partner-account-status';
import PartnerAccountBank from '~pages/dashboard/referral/partner-account/detail/partner-account-bank';
import PartnerAccountPayout from '~pages/dashboard/referral/partner-account/detail/partner-account-payout';
import PaymentPreferenceEditor from '~pages/dashboard/referral/partner-account/detail/payment-preference-editor';
import PromotionEditor from '~pages/dashboard/referral/partner-account/detail/partner-promotion';
import { PartnerType } from '~constants/partner';
import { makeStyles } from '@material-ui/styles';
const useStyles = makeStyles(
  theme => ({
    tagScheduled: {
      textTransform: 'capitalize',
      background: theme.colors.orange1,
      border: '1px solid',
      borderColor: theme.colors.orange3,
      color: theme.colors.orange6,
    },
    tagActive: {
      textTransform: 'capitalize',
      background: theme.colors.green1,
      border: '1px solid',
      borderColor: theme.colors.green3,
      color: theme.colors.green6,
    },
    tagExpired: {
      textTransform: 'capitalize',
      background: theme.colors.gray2,
      border: '1px solid',
      borderColor: theme.colors.gray5,
      color: 'rgba(0, 0, 0, 0.45)',
    },
  }),
  { name: 'PromotionTags' },
);
const PartnerAccountDetail = ({ partnerId }) => {
  const { t } = useTranslation();
  const { authRole } = useCurrentRoles();
  const [selectedTab, setSelectedTab] = useState('bank_account');
  const [showPartnerOwnerEditor, setShowPartnerOwnerEditor] = useState(false);
  const [partnerOwnerEditorParams, setPartnerOwnerEditorParams] = useState(null);
  // const [currentStatus, setCurrentStatus] = useState(status);
  const [showEditor, setShowEditor] = useState(false);

  const [showPaymentPreferenceEditor, setPaymentPreferenceEditor] = useState(false);

  const {
    data: partnerAccountResponse,
    loading: partnerAccountLoading,
    refetch: refetchList,
  } = useQuery(QUERY_LIST_PARTNERS, {
    variables: {
      partnerCode: partnerId,
    },
    onError: e => {
      message.error(e?.toString());
    },
    fetchPolicy: 'no-cache',
  });

  const partnerNode = partnerAccountResponse?.listPartners?.edges[0]?.node;

  const handleTableChange = key => {
    setSelectedTab(key);
  };

  const handleEditeAccountOwner = useCallback((id, currentOwnerId) => {
    setShowPartnerOwnerEditor(true);
    setPartnerOwnerEditorParams({
      partnerId: id,
      currentOwnerId: currentOwnerId,
    });
  }, []);

  const showEdit = ['regional_bd_head', 'admin', 'bd_admin']?.indexOf(authRole) > -1;

  return (
    <div className="partner-details">
      <Choose>
        <When condition={partnerAccountLoading || !partnerNode}>
          <div className="partner-details__loading">
            <Spin />
          </div>
        </When>
        <Otherwise>
          <div className="partner-details__header">
            <div className="partner-details__bread-crumb">
              <Breadcrumb>
                <Breadcrumb.Item>
                  <Link
                    className="partner-details__bread-crumb-text"
                    to={generatePath('referral.partnerAccount', {})}
                  >
                    {t('cms.sidebar.menu.referral.partner_account')}
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <span className="partner-details__bread-crumb-text">{partnerNode?.name}</span>
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
            <div className="title-wrapper">
              <h2 className="partner-details__title">{partnerNode?.name}</h2>
              <StatusBar
                t={t}
                status={partnerNode?.enabled}
                showEdit={showEdit}
                onClick={() => {
                  setShowEditor(true);
                }}
              />
            </div>
            <div className="partner-details__header-content">
              <div className="partner-details__info-row partner-details__flex-space-between">
                <div className="partner-details__info-row">
                  <div className="partner-details__info-col">
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner_account.market')}: ${
                        partnerNode?.market ||
                        t('cms.referral.partner.list.column.register_city.blank')
                      }`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner_account.partner_type')}: ${
                        PartnerType[partnerNode?.type] || '-'
                      }`}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner.list.column.partner_code')}: ${
                        partnerNode?.partnerCode
                      }`}
                    </span>
                  </div>

                  <div className="partner-details__info-col">
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner_account.partner_country')}: `}
                      <span>{partnerNode?.country || '-'}</span>
                    </span>

                    <span className="partner-details__info">
                      {`${t('cms.referral.partner_account.payment_preference')}: ${
                        partnerNode?.paymentPreference
                          ? partnerNode?.paymentPreference.charAt(0).toUpperCase() +
                            partnerNode?.paymentPreference.slice(1).toLowerCase()
                          : '-'
                      }`}
                      {showEdit ? (
                        partnerNode?.type !== 'INDIVIDUAL' ? (
                          <Tooltip
                            placement="top"
                            title={'This partner doesn’t have a Lianmeng account.'}
                          >
                            <span
                              className="partner-details__info-col__link"
                              style={{ color: 'rgba(0, 0, 0, 0.25)', cursor: 'default' }}
                            >
                              {t('cms.referral.partner.list.column.actions.edit')}
                            </span>
                          </Tooltip>
                        ) : (
                          <span
                            className="partner-details__info-col__link"
                            onClick={() => {
                              setPaymentPreferenceEditor(true);
                            }}
                          >
                            {t('cms.referral.partner.list.column.actions.edit')}
                          </span>
                        )
                      ) : (
                        <></>
                      )}
                    </span>
                    <span className="partner-details__info">
                      {`${t('cms.referral.partner_account.account_owner')}: ${
                        (partnerNode?.internalReferrer?.firstName ?? '') +
                        ' ' +
                        (partnerNode?.internalReferrer?.lastName ?? '')
                      }`}
                      {showEdit && (
                        <span
                          className="partner-details__info-col__link"
                          onClick={() =>
                            handleEditeAccountOwner(
                              partnerNode.id,
                              partnerNode?.internalReferrer?.id,
                            )
                          }
                        >
                          {t('cms.referral.partner.list.column.actions.edit')}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <Tabs
                className="partner-details__tabs"
                activeKey={selectedTab}
                onChange={handleTableChange}
              >
                <Tabs.TabPane
                  tab={t('cms.referral.partner_account.tab.bank_account')}
                  key="bank_account"
                />
                <Tabs.TabPane
                  tab={t('cms.referral.partner_account.tab.promotion')}
                  key="promotion"
                />
                <Tabs.TabPane
                  tab={t('cms.referral.partner_account.tab.commission_plan')}
                  key="commission_plan"
                />
                <Tabs.TabPane
                  tab={t('cms.referral.partner_account.tab.payout_plan')}
                  key="payout_plan"
                />
              </Tabs>
            </div>
          </div>
          <div>
            <Choose>
              <When condition={selectedTab === 'bank_account'}>
                <Choose>
                  <When condition={partnerNode?.partnerBanks}>
                    <BankAccount
                      showEdit={showEdit}
                      partnerId={partnerNode.id}
                      data={partnerNode?.partnerBanks[0]}
                      reFresh={() => {
                        refetchList();
                      }}
                    />
                  </When>
                  <Otherwise>
                    <div className="partner-detail-tab-card partner-detail-tab-card__body">
                      <Empty />
                    </div>
                  </Otherwise>
                </Choose>
              </When>
              <When condition={selectedTab === 'promotion'}>
                <PromotionCard
                  partnerId={partnerNode.id}
                  data={partnerNode?.partnerPromotions}
                  country={partnerNode?.country}
                  showEdit={showEdit}
                  paymentPreference={partnerNode?.paymentPreference}
                  reFresh={() => {
                    refetchList();
                  }}
                />
              </When>

              <When condition={selectedTab === 'commission_plan'}>
                <Choose>
                  <When condition={partnerNode?.commissionPlan && partnerNode?.currentTier}>
                    <CommissionCard
                      commission={partnerNode?.commissionPlan}
                      currentTierId={partnerNode?.currentTierId}
                      currentTierName={partnerNode?.currentTier?.name}
                      partnerId={partnerNode?.id}
                      showEdit={showEdit}
                      countryCode={partnerNode?.country}
                      reFresh={() => {
                        refetchList();
                      }}
                    />
                  </When>
                  <Otherwise>
                    <div className="partner-detail-tab-card partner-detail-tab-card__body">
                      <Empty />
                    </div>
                  </Otherwise>
                </Choose>
              </When>

              <When condition={selectedTab === 'payout_plan'}>
                <Choose>
                  <When condition={partnerNode?.payoutPlan}>
                    <PayoutPlanCard
                      country={partnerNode?.country}
                      plan={partnerNode?.payoutPlan}
                      partnerId={partnerNode.id}
                      showEdit={showEdit}
                    />
                  </When>
                  <Otherwise>
                    <div className="partner-detail-tab-card partner-detail-tab-card__body">
                      <Empty />
                    </div>
                  </Otherwise>
                </Choose>
              </When>
            </Choose>
          </div>
          <If condition={showPartnerOwnerEditor}>
            <PartnerAccountOwnerEditor
              activeModal
              t={t}
              editParams={partnerOwnerEditorParams}
              handleCloseModal={() => {
                setShowPartnerOwnerEditor(false);
                refetchList();
              }}
            />
          </If>
          <If condition={showEditor}>
            <PartnerAccountStatus
              activeModal
              t={t}
              editParams={{
                partnerId: partnerNode.id,
                status: partnerNode?.enabled,
              }}
              handleCloseModal={() => {
                setShowEditor(false);
                refetchList();
              }}
            />
          </If>
          <If condition={showPaymentPreferenceEditor}>
            <PaymentPreferenceEditor
              activeModal
              t={t}
              editParams={{
                partnerId: partnerNode.id,
                paymentPreference: partnerNode?.paymentPreference,
              }}
              handleCloseModal={() => {
                setPaymentPreferenceEditor(false);
                refetchList();
              }}
            />
          </If>
        </Otherwise>
      </Choose>
    </div>
  );
};

const CommissionCard = ({
  commission,
  currentTierId,
  partnerId,
  countryCode,
  currentTierName,
  reFresh,
  showEdit,
}) => {
  const { t } = useTranslation();
  const [showPartnerCommissionPlanEditor, setShowPartnerCommissionPlanEditor] = useState(false);
  const [editParams, setEditParams] = useState({
    partnerId: null,
    partnerCountryCode: null,
    currentCommissionId: null,
  });

  const handleEdit = useCallback((partnerId, partnerCountryCode, commissionPlanId) => {
    setShowPartnerCommissionPlanEditor(true);
    setEditParams({
      partnerId: partnerId,
      partnerCountryCode: partnerCountryCode,
      currentCommissionId: commissionPlanId,
    });
  }, []);

  const handleClosePartnerCommissionPlanEditorModal = useCallback(() => {
    setShowPartnerCommissionPlanEditor(false);
    reFresh && reFresh();
  }, []);

  const tierCalculationType = commission?.tiers[0]?.calculationRules[0]?.calculationType;

  return (
    <>
      <div className="partner-detail-tab-card">
        <div className="partner-detail-tab-card__header">
          <div className="partner-detail-tab-card__header__title">
            {t('cms.referral.partner.detail.tab.commission.title')}
          </div>
          <If condition={showEdit}>
            <PartnerDetailEditIcon
              onClick={() => handleEdit(partnerId, countryCode, commission.id)}
              className="partner-detail-tab-card__header__icon"
            />
          </If>
        </div>
        <div className="partner-detail-tab-card__body">
          <div className="partner-detail-tab-card__custom-table">
            <div className="partner-detail-tab-card__custom-table__header">
              <div className="partner-detail-tab-card__custom-table--flex partner-detail-tab-card__custom-table--w-250 partner-detail-tab-card__custom-table--flex-shrink-0">
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__list__column--divider partner-detail-tab-card__custom-table--w-full">
                  {t('cms.referral.partner.detail.tab.commission.table.header.name')}
                </div>
              </div>
              <div className="partner-detail-tab-card__custom-table--flex partner-detail-tab-card__custom-table--w-full">
                <div className="partner-detail-tab-card__custom-table--w-52" />
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-226">
                  {t('cms.referral.partner.detail.tab.commission.table.header.bookingsNo')}
                </div>
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-200">
                  {t('cms.referral.partner.detail.tab.commission.table.header.longTerm', {
                    currency: tierCalculationType === 'PERCENTAGE' ? '%' : commission.currency,
                  })}
                </div>
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-200">
                  {t('cms.referral.partner.detail.tab.commission.table.header.shortTerm', {
                    currency: tierCalculationType === 'PERCENTAGE' ? '%' : commission.currency,
                  })}
                </div>
                <div className="partner-detail-tab-card__list__column partner-detail-tab-card__custom-table--w-200">
                  {t('cms.referral.partner.detail.tab.commission.table.header.tierName')}
                </div>
              </div>
            </div>
            <div className="partner-detail-tab-card__custom-table__body">
              <div className="partner-detail-tab-card__custom-table__left partner-detail-tab-card__custom-table--w-250">
                <div className="partner-detail-tab-card__custom-table__column-body">
                  {commission.name}（{currentTierName}）
                  {commission.default ? (
                    <Tag className="partner-detail-tab-card__default-tag">
                      {t('cms.referral.commission_plan.list_card.tag.default')}
                    </Tag>
                  ) : null}
                </div>
              </div>
              <div className="partner-detail-tab-card__custom-table__right">
                {commission.tiers.map((tier, tierIndex) => (
                  <div
                    key={tier.name}
                    className={classNames('partner-detail-tab-card__custom-table__row', {
                      'partner-detail-tab-card__custom-table__row--active':
                        currentTierId === tierIndex,
                    })}
                  >
                    <div className="partner-detail-tab-card__custom-table__select-icon-column partner-detail-tab-card__custom-table--w-52 partner-detail-tab-card__custom-table--flex partner-detail-tab-card__custom-table--flex-center">
                      {currentTierId === tierIndex ? (
                        <CreateTick width="16" height="12" fill="#38B2A6" />
                      ) : null}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-226">
                      {tierIndex === commission.tiers.length - 1
                        ? `≥ ${tier.bookingCountFrom}`
                        : `${tier.bookingCountFrom} - ${tier.bookingCountTo}`}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-200">
                      {tier.longTermCalculationValue}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-200">
                      {tier.shortTermCalculationValue}
                    </div>
                    <div className="partner-detail-tab-card__custom-table__column-body partner-detail-tab-card__custom-table--w-200">
                      {tier.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <If condition={showPartnerCommissionPlanEditor}>
        <PartnerCommissionPlanEditor
          activeModal
          t={t}
          editParams={editParams}
          handleCloseModal={handleClosePartnerCommissionPlanEditorModal}
        />
      </If>
    </>
  );
};

const PayoutPlanCard = ({ country, plan, partnerId, showEdit }) => {
  const { t } = useTranslation();
  const [showEditor, setShowEditor] = useState(false);
  const tableColumns = useMemo(() => {
    return [
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t('cms.referral.partner.detail.tab.payout.table.header.name')}
          </div>
        ),
        dataIndex: 'name',
        key: 'name',
        // width: 140,
        className: 'partner-detail-tab-card__list__column',
        render: (text, record) => (
          <span className="partner-detail-tab-card__list__column-body">
            {text}
            {record.default ? (
              <Tag className="partner-detail-tab-card__default-tag">
                {t('cms.referral.commission_plan.list_card.tag.default')}
              </Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t(
              'cms.referral.partner.detail.tab.payout.table.header.percentagePaidAfterOppsCompleted',
            )}
          </div>
        ),
        dataIndex: 'bookingCompletedPercent',
        key: 'bookingCompletedPercent',
        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body">{`${
            text ? text + '%' : '-'
          }`}</span>
        ),
      },
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t(
              'cms.referral.partner.detail.tab.payout.table.header.percentagePaidAfterLandlordCommissionPaid',
            )}
          </div>
        ),
        dataIndex: 'bookingPaidPercent',
        key: 'bookingPaidPercent',
        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body">{`${
            text ? text + '%' : '-'
          }`}</span>
        ),
      },
    ];
  }, []);

  return (
    <div className="partner-detail-tab-card">
      <div className="partner-detail-tab-card__header">
        <div className="partner-detail-tab-card__header__title">Payout Plan</div>
        {showEdit && (
          <PartnerDetailEditIcon
            onClick={() => {
              setShowEditor(true);
            }}
            className="partner-detail-tab-card__header__icon"
          />
        )}
      </div>
      <div className="partner-detail-tab-card__body">
        <Table
          columns={tableColumns}
          size="middle"
          className="table-list partner-detail-tab-card__table"
          pagination={false}
          dataSource={[plan]}
        />
      </div>
      <If condition={showEditor}>
        <PartnerAccountPayout
          activeModal
          t={t}
          country={country}
          partnerId={partnerId}
          payoutPlanId={plan.id}
          handleCloseModal={() => {
            setShowEditor(false);
          }}
        />
      </If>
    </div>
  );
};

const BankAccount = ({ partnerId, data, reFresh, showEdit }) => {
  const { t } = useTranslation();
  const [showEditor, setShowEditor] = useState({ visible: false });
  const tableColumns = useMemo(() => {
    return [
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t('cms.referral.partner_account.bank_account.partner_bank_name')}
          </div>
        ),
        dataIndex: 'bankName',
        key: 'bankName',

        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body">{text || '-'}</span>
        ),
      },
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t('cms.referral.partner_account.bank_account.Partner_beneficiary_name')}
          </div>
        ),
        dataIndex: 'beneficiaryName',
        key: 'beneficiaryName',
        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body"> {text || '-'}</span>
        ),
      },
      {
        title: (
          <div className="partner-detail-tab-card__list__column-header">
            {t('cms.referral.partner_account.bank_account.partner_bank_account_NO')}
          </div>
        ),
        dataIndex: 'card',
        key: 'card',
        className: 'partner-detail-tab-card__list__column',
        render: text => (
          <span className="partner-detail-tab-card__list__column-body"> {text || '-'}</span>
        ),
      },
    ];
  }, []);

  return (
    <div className="partner-detail-tab-card">
      {data && (
        <div className="partner-detail-tab-card__header">
          <div className="partner-detail-tab-card__header__title">Bank Account</div>
          {showEdit && (
            <PartnerDetailEditIcon
              onClick={() => {
                setShowEditor({ visible: true });
              }}
              className="partner-detail-tab-card__header__icon"
            />
          )}
        </div>
      )}
      <If condition={showEditor.visible}>
        <PartnerAccountBank
          activeModal
          t={t}
          partnerId={partnerId}
          cardId={data?.id}
          editParams={{
            bankName: data?.bankName,
            beneficiaryName: data?.beneficiaryName,
            card: data?.card,
          }}
          handleCloseModal={() => {
            setShowEditor({ visible: false });
            reFresh && reFresh();
          }}
        />
      </If>
      <Choose>
        <When condition={data}>
          <div className="partner-detail-tab-card__body">
            <Table
              columns={tableColumns}
              size="middle"
              className="table-list partner-detail-tab-card__table"
              pagination={false}
              dataSource={data ? [data] : []}
            />
          </div>
        </When>
        <Otherwise>
          <div className="bank-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>There is no bank account now.</span>}
            >
              <Button
                icon="plus"
                onClick={() => {
                  setShowEditor({ visible: true });
                }}
              >
                Add bank account
              </Button>
            </Empty>
          </div>
        </Otherwise>
      </Choose>
    </div>
  );
};

const PromotionCard = ({ partnerId, country, data, reFresh, paymentPreference, showEdit }) => {
  const { t } = useTranslation();
  const [showPromotionEditor, setShowPromotionEditor] = useState(false);
  const [editParams, setEditParams] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const classes = useStyles();
  const [deletePartnerPromotion, { loading: deletePartnerPromotionLoading }] = useMutation(
    DELETE_PARTNER_PROMOTION,
    {
      onError: e => {
        message.error(e.message);
      },
    },
  );
  return (
    <>
      <div style={{ margin: '1.5rem 1.5rem 0 1.5rem' }}>
        <If condition={data?.length > 0}>
          <Button
            type="dashed"
            icon="plus"
            style={{
              borderColor: '#38B2A6',
              color: '#38B2A6',
              background: 'rgba(0,0,0,0)',
              height: '40px',
            }}
            onClick={() => {
              setShowPromotionEditor(true);
            }}
            block
          >
            Assign New Promotion
          </Button>
        </If>
      </div>

      <div className="partner-detail-tab-card">
        {showPromotionEditor && (
          <PromotionEditor
            activeModal
            t={t}
            paymentPreference={paymentPreference}
            partnerId={partnerId}
            country={country}
            params={editParams}
            seletedPromotionId={null}
            handleCloseModal={() => {
              setShowPromotionEditor(false);
              reFresh && reFresh();
            }}
          />
        )}

        <Choose>
          <When condition={data?.length > 0}>
            <div style={{ backgroundColor: '#F8F8F6' }}>
              {data.map((item, key) => (
                <Card
                  style={{ marginBottom: '24px' }}
                  headStyle={{ padding: '0 32px' }}
                  bodyStyle={{ padding: '20px 32px  ' }}
                  key={key}
                  title={item.referralCommissionPlan.name}
                  bordered={false}
                  extra={
                    showEdit ? (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FormOutlined
                          onClick={() => {
                            setEditParams({
                              id: item.id,
                            });
                            setShowPromotionEditor(true);
                            reFresh && reFresh();
                          }}
                          style={{ fontSize: '18px', color: '#38B2A6' }}
                        />
                        <Divider style={{ margin: '0 12px' }} type="vertical" />
                        <Popover
                          placement="topRight"
                          trigger="click"
                          visible={showDelete === item.id}
                          onVisibleChange={v => {
                            if (!v) {
                              setShowDelete(null);
                            }
                          }}
                          content={
                            <div className="partner-details__popover">
                              <div className="wrapper">
                                <ExclamationCircleFilled
                                  className="icon"
                                  style={{ color: '#F9A600' }}
                                />

                                <div className="txt">
                                  Confirm to unlink this promotion to the partner? click confirm to
                                  delete, click cancel to stop the process
                                </div>
                              </div>
                              <div className="btn_group">
                                <Button
                                  type="ghost"
                                  onClick={() => {
                                    setShowDelete(null);
                                  }}
                                >
                                  No
                                </Button>
                                <Button
                                  loading={deletePartnerPromotionLoading}
                                  type="danger"
                                  style={{ backgroundColor: '#FF4D4F' }}
                                  onClick={async () => {
                                    const result = await deletePartnerPromotion({
                                      variables: { id: item.id },
                                    });
                                    if (result?.data?.deletePartnerPromotion) {
                                      message.success(
                                        t(
                                          'cms.referral.partner.detail.editor.action.partner_status.toast.success',
                                        ),
                                      );
                                      setShowDelete(null);
                                      reFresh && reFresh();
                                    }
                                  }}
                                >
                                  Yes, unlink
                                </Button>
                              </div>
                            </div>
                          }
                        >
                          <DeleteOutlined
                            onClick={() => {
                              setShowDelete(item.id);
                            }}
                            style={{ color: 'red', fontSize: '18px' }}
                          />
                        </Popover>
                      </div>
                    ) : (
                      <></>
                    )
                  }
                >
                  {item?.referralCommissionPlan?.paymentPreference !== 'NOT_SPECIFIC' &&
                    paymentPreference !== item?.referralCommissionPlan?.paymentPreference && (
                      <div className="partner-details__tooltip-wrapper">
                        <Icon
                          type="exclamation-circle"
                          theme="filled"
                          style={{ color: '#F5222D', marginRight: 8 }}
                        />
                        <div className="tooltip-word">
                          The partner&#39;s payment preference doesn&#39;t match the promotion&#39;s
                          requirements, so it can&#39;t be applied.
                        </div>
                      </div>
                    )}

                  <Descriptions column={2}>
                    <Descriptions.Item label="Promotion Name">
                      {item.referralCommissionPlan.name}
                      <Tag
                        style={{ marginLeft: '8px' }}
                        className={classNames('promotion-list__body__list__tag', {
                          [classes.tagActive]: item.referralCommissionPlan?.status === 'ACTIVE',
                          [classes.tagScheduled]:
                            item.referralCommissionPlan?.status === 'SCHEDULED',
                          [classes.tagExpired]: item.referralCommissionPlan?.status === 'EXPIRED',
                        })}
                      >
                        {item.referralCommissionPlan?.status?.toLowerCase() || '-'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="External Name">
                      {item.referralCommissionPlan.externalName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Applied Country">
                      {item.referralCommissionPlan.country.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Partner Payment Preference">
                      {item.referralCommissionPlan.paymentPreference}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={`Long Term (${item.referralCommissionPlan?.currency})`}
                    >
                      {item.referralCommissionPlan.tiers[0].longTermCalculationValue}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={`Short Term (${item.referralCommissionPlan?.currency})`}
                    >
                      {item.referralCommissionPlan.tiers[0].shortTermCalculationValue}
                    </Descriptions.Item>
                    <Descriptions.Item label="Valid Date">
                      {formatDay(dayFormatType.UKFORMAT, item.referralCommissionPlan.validDateFrom)}{' '}
                      - {formatDay(dayFormatType.UKFORMAT, item.referralCommissionPlan.validDateTo)}
                    </Descriptions.Item>
                  </Descriptions>

                  <Divider />
                  <div>
                    {'Destination Country/City:' +
                      ' ' +
                      item.referralCommissionPlan?.bookingDestinationCountry?.name +
                      ' - ' +
                      item.referralCommissionPlan?.bookingDestinationCities
                        ?.map(it => it.name)
                        .join(',')}
                  </div>
                  <Descriptions column={2} style={{ marginTop: '16px' }}>
                    <Descriptions.Item label=" Opps Submission Date">
                      {item.referralCommissionPlan.tiers[0].bookingSubmittedDateFrom} -{' '}
                      {item.referralCommissionPlan.tiers[0].bookingSubmittedDateTo}
                    </Descriptions.Item>
                    <Descriptions.Item label="Opps Completed Date">
                      {item.referralCommissionPlan.tiers[0].bookingCompletedDateFrom} -{' '}
                      {item.referralCommissionPlan.tiers[0].bookingCompletedDateTo}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </div>
          </When>
          <Otherwise>
            <div className="bank-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span>There is no promotion now.</span>}
              >
                <Button
                  icon="plus"
                  disabled={!showEdit}
                  onClick={() => {
                    setShowPromotionEditor(true);
                  }}
                >
                  Assign Promotion
                </Button>
              </Empty>
            </div>
          </Otherwise>
        </Choose>
      </div>
    </>
  );
};

const StatusBar = ({ t, status, onClick, showEdit }) => {
  return (
    <div className="status-bar">
      <div>Partner Account Status:</div>
      <div className="status">
        {status ? (
          <Icon type="check" style={{ color: '#38B2A6' }} />
        ) : (
          <Icon type="close" style={{ color: '#BFBFBF' }} />
        )}
      </div>
      <div>{status ? 'Enable' : 'Disabled'}</div>

      {showEdit && (
        <>
          <Divider type="vertical" />
          <span className="edditor" onClick={onClick}>
            {t('cms.referral.partner.list.column.actions.edit')}
          </span>
        </>
      )}
    </div>
  );
};

export default PartnerAccountDetail;
